import { List, ListPlace, Place, User } from '@prisma/client';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import Footer from '../common/Footer';
import Nav from '../common/nav';
import { RatingToThumbsIcon } from '../common/util';
import {prisma} from '@gwak/db';

type Profile = { user: User & { list_place: (ListPlace & { place: Place })[] } }

function Card({user}: Profile) {
  return (
    <div className="flex justify-center">
      <div className="flex h-full w-full flex-col justify-center gap-4 rounded-xl border py-4 px-3 md:w-3/4 md:p-6">
        <div className="flex items-center justify-between">
          <div>
            <Link
              href={`/profiles/${user.id}`}
              className="text-md font-medium text-blue-600 hover:underline"
            >
              <h3 className="mb-1 inline bg-gray-100 text-2xl font-bold leading-none text-gray-900">
                @{user.username}
              </h3>
            </Link>
            <p className="truncate text-md font-light text-gray-500">
              {user.profile}
            </p>
          </div>
          <Link
            href={`/profiles/${user.id}`}
            className="text-md font-medium text-blue-600 hover:underline"
          >
            View all
          </Link>
        </div>
        <div className="flow-root">
          <h5 className="text-lg mb-2 font-medium leading-none text-gray-900">
            Recently reviewed:
          </h5>
          <ul className="divide-y divide-gray-200 rounded-md border px-3 py-3 md:px-4">
            {user.list_place.map((lp, idx) => (
              <li className="py-2 sm:py-2" key={lp.id}>
                <div className="flex items-center space-x-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col">
                      <div className='flex justify-between items-center'>
                        <p className="text-lg font-semibold text-gray-900">
                          {idx + 1}. {lp.place.name}
                        </p>
                        <div className="text-sm text-gray-500">
                          {new Date(lp.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className='flex justify-between items-center'>
                        <p className="truncate text-sm font-light text-gray-500">
                          {lp.place.formatted_address}
                        </p>
                        {lp.rating ? <div className="ml-2 flex shrink-0 justify-end">
                          <div>{RatingToThumbsIcon[lp.rating]}</div>
                        </div> : null}
                      </div>
                    </div>

                    {lp.comments ? (
                      <p className="line-clamp-2 mt-2 text-md text-gray-900">
                        {lp.comments}
                      </p>
                    ) : null}
                  </div>
                </div>
              </li>
            ))
            }
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function Explore({profiles}: {profiles: (User & {list: (List & {list_place: (ListPlace & {place: Place})[]})[]})[] | undefined}) {
  let userListPlaces;
  if (profiles) {
    userListPlaces = profiles
      .map((u) => {
        const listPlaces = u.list.flatMap((l) => l.list_place);
        listPlaces.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        return {
          ...u,
          list_place: listPlaces.slice(0, 3),
        };
      })
      .filter((u) => u.list_place.length > 0);

    userListPlaces.sort(
      (a, b) =>
        Math.max(...b.list_place.map((lp) => new Date(lp.updated_at).getTime())) -
        Math.max(...a.list_place.map((lp) => new Date(lp.updated_at).getTime()))
    );
  }

  return (
    <>
      <Head>
        <title>Explore profiles | Gwak</title>
        <meta name="description" content="Explore some recently reviewed restaurants on Gwak."/>
      </Head>
      <Nav/>
      <section className="container mx-auto w-full max-w-4xl md:px-4">
        <Image
          className="absolute top-0 left-0 mt-24 hidden lg:block"
          src="/page-assets/icons/dots/blue-dot-left-bars.svg"
          alt=""
          width="236" height="102"
        />
        <Image
          className="absolute top-0 right-0 mt-40 hidden lg:block"
          src="/page-assets/icons/dots/yellow-dot-right-shield.svg"
          alt=""
          width="172" height="116"
        />
        <h1 className="font-heading mb-10 px-4 text-4xl font-semibold">
          Explore profiles
        </h1>
        <div className="mx-auto flex w-full flex-col gap-4 align-middle">
          {userListPlaces?.map((u) => (
            <Card key={u.id} user={u}/>
          ))}
        </div>
      </section>
      <Footer/>
    </>
  );
}

export async function getStaticProps() {
  const profiles = await prisma.user.findMany({
    where: {
      is_deleted: false,
      id: {
        notIn: [
          '9ba3cb5d-8dd2-4018-ac03-0e9a4d02fc95',
          '5123ba34-3ee9-4339-9151-1f23febdb75e',
          '937bf2e8-28cd-40e9-a92a-3b69122b1cb4',
          '0e175674-8b9e-4d4e-a45e-cb2556fddf22',
          'f137307d-7cd1-4cc7-9f5d-c17573af00d9',
          '736718a7-2300-483c-aaad-884739a53ab8',
        ],
      },
    },
    include: {
      list: {
        include: {
          list_place: {
            include: {
              place: true,
            },
            orderBy: {
              updated_at: 'desc',
            },
            take: 3,
          },
        },
      },
    },
  });


  return {
    props: {
      profiles: JSON.parse(JSON.stringify(profiles)),
    },
    revalidate: 60, // In seconds
  }
}
